import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api, apiError } from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = anonymous
  const { i18n } = useTranslation();

  const applyLang = useCallback((lang) => {
    if (lang && lang !== i18n.language) {
      i18n.changeLanguage(lang);
      localStorage.setItem("vy_lang", lang);
    }
  }, [i18n]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // A language picked on the device wins: it may not have reached the server yet (offline).
      applyLang(localStorage.getItem("vy_lang") || data.language);
      return data;
    } catch (e) {
      // A network failure (offline field use) must not log the Ambassador out.
      if (!e?.response) return user || null;
      setUser(false);
      return false;
    }
  }, [applyLang, user]);

  useEffect(() => { refresh(); /* session check runs once on mount */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      applyLang(data.user.language);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  const setLanguage = async (lang) => {
    localStorage.setItem("vy_lang", lang);
    applyLang(lang);
    if (user) {
      try {
        const { data } = await api.patch("/auth/profile", { language: lang });
        setUser({ ...data, language: lang });
      } catch { /* offline: the local preference stands and syncs on the next change */ }
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, refresh, setLanguage }}>
      {children}
    </AuthContext.Provider>
  );
};
